import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ShieldCheck, Loader2, Lock, Star, Info } from 'lucide-react';

// YOUR STRIPE KEY
const stripePromise = loadStripe('pk_test_51SrbzbGTbAQw0BiZj0WigdXIofCo47Ej6VNvMS67DJgjcuBZESkP8kIrx89todhtP2G3t09WQZWbUNk5DH1kxHNc00uAcUNtHq');
//const API_BASE_URL = 'http://localhost:8000/api'; 

const PRICING_TIERS = {
    core: { monthly: 390, yearly: 350 },
    pro: { monthly: 1000, yearly: 900 },
    enterprise: { monthly: 2250, yearly: 1950 }
};

// --- FORMS ---

const PaymentForm = ({ onSubmit, loading, price }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardError, setCardError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        const cardElement = elements.getElement(CardElement);
        const { error, paymentMethod } = await stripe.createPaymentMethod({ type: 'card', card: cardElement });
        if (error) setCardError(error.message);
        else onSubmit(paymentMethod.id);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Card Details</label>
                <div style={{ padding: '14px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white' }}>
                    <CardElement options={{ 
                        style: { 
                            base: { fontSize: '16px', color: '#1e293b', fontFamily: '"Inter", sans-serif', '::placeholder': { color: '#94a3b8' } },
                            invalid: { color: '#ef4444' }
                        } 
                    }} />
                </div>
            </div>
            {cardError && <div style={{ padding: '10px', background: '#fee2e2', color: '#ef4444', borderRadius: '6px', marginBottom: '20px', fontSize: '0.9rem' }}>{cardError}</div>}
            
            <button type="submit" disabled={!stripe || loading} style={buttonStyle}>
                {loading ? <Loader2 className="animate-spin" /> : (
                    <>Pay £{price} Securely <Lock size={16} /></>
                )}
            </button>
            <div style={{ marginTop: '15px', textAlign: 'center', fontSize: '0.8rem', color: '#64748b' }}>
                Encrypted via 128-bit SSL. Powered by Stripe.
            </div>
        </form>
    );
};

const QuoteForm = ({ onSubmit, loading }) => (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <div style={{ padding: '20px', background: '#f0f9ff', borderRadius: '8px', marginBottom: '25px', border: '1px solid #bae6fd' }}>
            <h4 style={{ color: '#0369a1', margin: '0 0 10px 0', fontSize: '0.95rem' }}>Enterprise Proposal</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#0c4a6e', lineHeight: '1.5' }}>
                Our enterprise team will review your requirements and send a formal proposal within 24 hours. No payment required today.
            </p>
        </div>
        <button type="submit" disabled={loading} style={{...buttonStyle, background: '#0f172a'}}>
            {loading ? <Loader2 className="animate-spin" /> : 'Request Custom Quote'}
        </button>
    </form>
);

const TrialForm = ({ onSubmit, loading }) => (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <div style={{ padding: '20px', background: '#ecfdf5', borderRadius: '8px', marginBottom: '25px', border: '1px solid #6ee7b7' }}>
            <h4 style={{ color: '#047857', margin: '0 0 10px 0', fontSize: '0.95rem' }}>14-Day Free Trial</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#064e3b', lineHeight: '1.5' }}>
                No credit card required. Experience the full power of SiteACT on your own site. 
            </p>
        </div>
        <button type="submit" disabled={loading} style={{...buttonStyle, background: '#10b981'}}>
            {loading ? <Loader2 className="animate-spin" /> : 'Start Free Trial'}
        </button>
    </form>
);

// --- UNIVERSAL SETUP NOTICE ---
const SetupNotice = () => (
    <div style={{ 
        marginTop: '30px', padding: '16px', background: '#f8fafc', 
        borderRadius: '8px', border: '1px dashed #cbd5e1', 
        display: 'flex', gap: '12px', alignItems: 'flex-start' 
    }}>
        <Info size={20} color="#3b82f6" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ fontSize: '0.9rem', color: '#475569', lineHeight: '1.5' }}>
            <strong>Next Steps:</strong> The SiteACT team will contact you shortly after submission to assist with your custom site set-up and onboarding.
        </div>
    </div>
);

// --- MAIN PAGE ---

const Register = () => {
    const [searchParams] = useSearchParams();
    const rawPlan = searchParams.get('plan') || 'core';
    const plan = rawPlan.toLowerCase();
    const billing = searchParams.get('billing') || 'monthly';
    const mode = searchParams.get('mode') || 'payment'; // 'payment', 'quote', or 'trial'
    
    const monthlyPrice = PRICING_TIERS[plan] ? PRICING_TIERS[plan][billing] : 0;
    const amountDue = billing === 'yearly' ? monthlyPrice * 12 : monthlyPrice;

    const [formData, setFormData] = useState({ company: '', email: '', firstName: '', lastName: '' });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); 

    const handleRegister = async (paymentMethodId = null) => {
        setLoading(true);
        // ... (Simulated API Call - keep your existing fetch logic here)
        setTimeout(() => { setLoading(false); setStatus('success'); }, 2000); 
    };

    if (status === 'success') {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#f8fafc' }}>
                <div style={{ width: '80px', height: '80px', background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                    <ShieldCheck size={40} color="#16a34a" />
                </div>
                <h2 style={{ fontSize: '2rem', color: '#0f172a', fontWeight: '800' }}>Request Confirmed</h2>
                <p style={{ color: '#64748b', textAlign: 'center', maxWidth: '400px', lineHeight: '1.6', marginBottom: '30px' }}>
                    {mode === 'quote' ? "We've received your request. Check your email for next steps." : 
                     mode === 'trial' ? "Welcome to SiteACT. Our team will contact you shortly to set up your trial environment." :
                     "Welcome to SiteACT. Your secure environment is being provisioned."}
                </p>
                <button style={buttonStyle} onClick={() => window.location.href='/'}>Return to Home</button>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
            
            {/* LEFT SIDE - SUMMARY & TRUST */}
            <div style={{ flex: '1 1 400px', backgroundColor: '#0f172a', color: 'white', padding: 'clamp(40px, 5vw, 80px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', color: '#3b82f6' }}>
                        <ShieldCheck size={32} />
                        <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}>SiteACT</span>
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: '30px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '20px' }}>
                            <div>
                                <div style={{ fontSize: '0.9rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>Selected Plan</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', textTransform: 'capitalize' }}>{plan} Edition</div>
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                                {mode === 'trial' ? '£0' : `£${monthlyPrice}`}
                                <span style={{fontSize: '1rem', fontWeight: 'normal'}}>{mode === 'trial' ? '' : '/mo'}</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1' }}>
                            <span>Total due today</span>
                            <span style={{ color: '#fff', fontWeight: 'bold' }}>
                                £{mode === 'payment' ? amountDue.toLocaleString() : '0.00'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* TESTIMONIAL - The "Expensive" Touch */}
                <div style={{ marginTop: '40px' }}>
                    <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
                        {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="#fbbf24" stroke="none" />)}
                    </div>
                    <p style={{ fontStyle: 'italic', lineHeight: '1.6', color: '#e2e8f0', marginBottom: '20px' }}>
                        "We switched from HammerTech to SiteACT last year. The no-app workflow for workers saved us roughly 15 hours a week in onboarding time. It pays for itself."
                    </p>
                    <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                         <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>JD</div>
                         <div>
                            <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>James Davies</div>
                            <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Ops Director, Metro Build</div>
                         </div>
                    </div>
                </div>
            </div>

            {/* RIGHT SIDE - FORM */}
            <div style={{ flex: '1.2 1 500px', backgroundColor: '#f8fafc', padding: 'clamp(40px, 5vw, 80px)', display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '100%', maxWidth: '500px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '30px', color: '#0f172a' }}>
                        {mode === 'quote' ? 'Request Enterprise Quote' : 
                         mode === 'trial' ? 'Start Your Free Trial' : 
                         'Finalize Payment'}
                    </h2>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                        <Input label="First Name" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})} placeholder="Jane" />
                        <Input label="Last Name" value={formData.lastName} onChange={e => setFormData({...formData, lastName: e.target.value})} placeholder="Doe" />
                    </div>
                    <Input label="Company Name" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} placeholder="Acme Construction Ltd" style={{ marginBottom: '20px' }} />
                    <Input label="Work Email" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="jane@acme.com" style={{ marginBottom: '30px' }} />

                    {mode === 'payment' && (
                        <Elements stripe={stripePromise}>
                            <PaymentForm onSubmit={handleRegister} loading={loading} price={amountDue.toLocaleString()} />
                        </Elements>
                    )}
                    
                    {mode === 'quote' && (
                        <QuoteForm onSubmit={() => handleRegister()} loading={loading} />
                    )}

                    {mode === 'trial' && (
                        <TrialForm onSubmit={() => handleRegister()} loading={loading} />
                    )}

                    {/* Universal Setup Notice applied to all options */}
                    <SetupNotice />
                </div>
            </div>
        </div>
    );
};

const Input = ({ label, style, ...props }) => (
    <div style={style}>
        <label style={labelStyle}>{label}</label>
        <input style={{ 
            width: '100%', padding: '12px 16px', borderRadius: '8px', 
            border: '1px solid #cbd5e1', fontSize: '1rem', background: 'white',
            transition: 'border-color 0.2s', outline: 'none'
        }} required {...props} 
        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
        onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
        />
    </div>
);

const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '8px' };
const buttonStyle = {
    width: '100%', padding: '16px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: 'white', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'background 0.2s'
};

export default Register;